// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { z } from 'zod';

// Schema for validation error fields
const validationErrorFieldSchema = z.object({
  fieldName: z.string(),
  error: z.string(),
  errorCode: z.number(),
});

// Schema for row validation errors
const rowValidationErrorSchema = z.object({
  index: z.number(),
  errors: z.array(validationErrorFieldSchema),
});

// Schema for processed time import data
const processedTimeImportDataSchema = z.object({
  index: z.number(),
  totalMinutes: z.number(),
  category: z.string(),
  workerName: z.string(),
  date: z.string(),
  errors: z.array(validationErrorFieldSchema).nullable(),
});

// Schema for time upload response
const timeUploadResponseSchema = z.object({
  success: z.boolean(),
  items: z.array(processedTimeImportDataSchema),
  structureErrors: z.array(validationErrorFieldSchema),
  rowErrors: z.array(rowValidationErrorSchema),
});

export const uploadTimeDataResponseSchema = z.object({
  data: timeUploadResponseSchema,
});

export type TimeDataUploadResult = z.infer<typeof timeUploadResponseSchema>;

export async function uploadTimeData(
  formData: FormData
): Promise<TimeDataUploadResult> {
  const url = '/v1/import/time-data';
  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const parsed = await parseResponse(
    uploadTimeDataResponseSchema,
    response.data,
    {
      schemaName: 'UploadTimeData',
      endpoint: url,
    }
  );
  return parsed.data;
}
